import type { IMemoryView } from "../dotnet";
import { read } from "../common/persistence";

export interface MemoryView {
  _pointer: number;
}

// eslint-disable-next-line unused-imports/no-unused-vars
export interface Span<T> extends IMemoryView, MemoryView {
  _pointer: number;
}

export function binaryReader<T>(span: Span<T>): T {
  if (span.length === 0) {
    return null as T;
  }
  const { _pointer: ptr, byteLength } = span;
  const data = new DataView(Module.HEAPU8.buffer, Module.HEAPU8.byteOffset + ptr, byteLength);
  const parsed = read<T>(data);

  const payload = parsed.payload;

  return payload;
}

export type BinaryGetter<T extends unknown[], R> = (...args: T) => Span<R>;
export function useBinaryReader<TArgs extends unknown[], TResult>(getter: BinaryGetter<TArgs, TResult>): (...args: TArgs) => TResult;
export function useBinaryReader<TArgs extends unknown[], R>(getter: BinaryGetter<TArgs, Promise<R>>): (...args: TArgs) => Promise<R>;
export function useBinaryReader<TArgs extends unknown[], TResult>(getter: BinaryGetter<TArgs, TResult | Promise<TResult>>) {
  return (...args: TArgs) => {
    const result = getter(...args);

    if (result instanceof Promise) {
      return result.then(parseBinary);
    }

    return parseBinary(result);
  };
}

function parseBinary(result: Span<unknown>) {
  if (result == null) {
    return null;
  }

  try {
    return binaryReader(result);
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("Error reading binary data from bridge", error);
      throw error;
    }

    return null;
  }
}

export type JsonGetter<T extends unknown[], R> = (...args: T) => R;
export function useJsonReader<TArgs extends unknown[], TResult>(getter: JsonGetter<TArgs, TResult>): (...args: TArgs) => TResult;
export function useJsonReader<TArgs extends unknown[], R>(getter: JsonGetter<TArgs, Promise<R>>): (...args: TArgs) => Promise<R>;
export function useJsonReader<TArgs extends unknown[], TResult>(getter: JsonGetter<TArgs, TResult | Promise<TResult>>) {
  return (...args: TArgs) => {
    const result = getter(...args);

    if (result instanceof Promise) {
      return result.then(parseJson);
    }

    return parseJson(result);
  };
}

function parseJson(result: unknown) {
  if (result == null) {
    return null;
  }

  if (typeof result !== "string") {
    throw new TypeError("Expected JSON string");
  }

  return JSON.parse(result);
}
